import styles from './headline.module.scss';
import cn from 'classnames';
import { Breadcrumbs } from '@/ui-components/molecules/Breadcrumbs/Breadcrumbs';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { type Cols } from '@/ui-components/atoms/Layout/Grid';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Text } from '@/ui-components/atoms/Text/Text';
import { TextClasses, TextTags } from '@/ui-components/atoms/Text/interfaces';
import { ImageWrapper, getBackgroundImage } from '@/shared-components/ImageWrapper/ImageWrapper';

type HeadlineVariant = 'Hero' | 'Legal' | 'Listen';
export type Alignment = 'left' | 'center';

interface Props {
    backgroundColor?: string;
    backgroundImageMobile?: string;
    backgroundImageTablet?: string;
    textColor?: string;
    desktopCols: Cols;
    tag: TextTags;
    className?: TextClasses;
    children: React.ReactNode;
    eyebrow?: string;
    subhead?: string | React.ReactNode;
    subClassName?: TextClasses;
    mainContainerClassName?: string;
    standardMoleculeSpacing?: boolean;
    variant?: HeadlineVariant;
    breadcrumbs?: Link[];
    align?: Alignment;
    subHeadStyles?: string;
    foregroundImage?: {
        tablet?: Partial<ImageProps>;
        mobile?: Partial<ImageProps>;
        stickToBottom?: boolean;
    };
}

export default function Headline({
    backgroundColor,
    textColor,
    desktopCols = 12,
    tag,
    className,
    children,
    eyebrow,
    subhead,
    subClassName,
    mainContainerClassName,
    standardMoleculeSpacing,
    variant,
    breadcrumbs,
    align,
    backgroundImageTablet,
    backgroundImageMobile,
    subHeadStyles,
    foregroundImage,
}: Props): React.JSX.Element {
    const colStartLimiter = desktopCols < 10 ? 10 : desktopCols;
    const desktopStart = (12 - colStartLimiter) as Cols;
    const isListenVariant = variant === 'Listen';

    const mainWrapperStyles = cn(mainContainerClassName, {
        [styles.listenWrapper]: isListenVariant,
    });

    const containerClasses = cn(styles.headlineContainer, {
        [styles[`${variant}`]]: variant,
        [styles.withBreadcrumbs]: breadcrumbs,
    });

    const headingClasses = cn(styles.heading, {
        [styles.center]: align === 'center',
        [styles.pinToBottom]: isListenVariant,
    });

    const eyebrowElement = eyebrow ? (
        <Text tag="div" variant="eyebrowLg">
            {eyebrow}
        </Text>
    ) : null;

    const breadcrumbsItems = breadcrumbs?.map((e) => ({
        href: e.href,
        target: e.link_target,
        children: e.link_text,
    }));

    const content = isListenVariant ? (
        <div className={containerClasses}>
            <div className={styles.contentWrapper}>
                {breadcrumbsItems && <Breadcrumbs items={breadcrumbsItems} isWhiteBreadcrumbs />}
                {eyebrowElement}
                {/* image size optimised based on container */}
                <ImageWrapper
                    src={foregroundImage?.tablet?.source ?? ''}
                    alt={foregroundImage?.tablet?.altText ?? ''}
                    optimizedWidth={foregroundImage?.tablet?.width}
                    optimizedHeight={foregroundImage?.tablet?.height}
                    maxWidth={440}
                    className={styles.foregroundImageStyles}
                    loadingStrategy="eager"
                />
                <Text tag={tag} variant={className} className={headingClasses} dataCy="molecules-headline-text">
                    {children}
                </Text>
                {subhead && (
                    <Text
                        tag="div"
                        variant={subClassName}
                        className={subHeadStyles}
                        dataCy="molecules-headline-sub-text"
                    >
                        {subhead}
                    </Text>
                )}
            </div>
        </div>
    ) : (
        <Grid container className={containerClasses}>
            <Grid col={{ sm: 12, md: desktopCols }} start={{ md: desktopStart }}>
                {breadcrumbsItems && <Breadcrumbs items={breadcrumbsItems} isWhiteBreadcrumbs />}
                {eyebrowElement}
                <Text tag={tag} variant={className} className={headingClasses} dataCy="molecules-headline-text">
                    {children}
                </Text>
                {subhead && (
                    <Text
                        tag="div"
                        variant={subClassName}
                        className={subHeadStyles}
                        dataCy="molecules-headline-sub-text"
                    >
                        {subhead}
                    </Text>
                )}
            </Grid>
        </Grid>
    );

    return (
        <StyledContainer
            backgroundColor={backgroundColor}
            backgroundImageTablet={getBackgroundImage({ src: backgroundImageTablet })}
            backgroundImageMobile={getBackgroundImage({ src: backgroundImageMobile })}
            textColor={textColor}
            standardMoleculeSpacing={standardMoleculeSpacing}
            includeNavPadding={variant === 'Hero'}
            className={mainWrapperStyles}
            eagerHero={variant === 'Hero' || variant === 'Listen'}
        >
            {content}
        </StyledContainer>
    );
}
